import flatpickr from "flatpickr";
import { Instance } from "flatpickr/dist/types/instance";
import { Dom } from "../../../common/dom";
import { Helper } from "../../../common/helper";
import { Icon, IconHelper } from "../../../common/icon-helper";
import { EditControl } from "../../form-row";
import { DatePickerHelper } from "./date-picker-helper";

export class DateControl implements EditControl
{
    private readonly input: HTMLInputElement;
    private readonly inputRow: HTMLDivElement;
    private readonly dateGroup: HTMLDivElement;
    private fp: Instance;
    private readonly isNative: boolean;
    private onChangeAction = () =>
    {};

    constructor(placeholder?: string)
    {
        // On mobile we use the OS-native date input for the single date field;
        // everywhere else we use flatpickr to keep the DD.MM.YYYY look and feel.
        this.isNative = Helper.isMobile();

        this.inputRow = Dom.createDiv();
        this.input = Dom.create("input", "w-form-control", "w-date-picker-input");

        this.dateGroup = Dom.addDiv(this.inputRow, "w-input-group", "date", "w-date-picker-group");
        this.input.placeholder = placeholder || "";
        this.dateGroup.append(this.input);

        if (this.isNative)
        {
            this.input.type = "date";
            this.input.addEventListener("change", () => this.notifyChange());
            return;
        }

        this.input.type = "text";
        const addon = Dom.addSpan(this.dateGroup, "w-input-group-addon");
        Dom.addSpan(addon).className = IconHelper.getIconClass(Icon.Calendar);

        this.input.addEventListener("input", () => this.maskDateInput());

        this.fp = flatpickr(this.input, {
            ...DatePickerHelper.baseOptions(),
            allowInput: true,
            onChange: () => this.notifyChange()
        });
        this.fp.calendarContainer.classList.add("w-date-picker");
        this.input.closest(".flatpickr-wrapper")?.classList.add("w-date-picker-wrapper");

        addon.addEventListener("click", () => this.fp.open());
    }

    initialize(parent: HTMLElement)
    {
        parent.append(this.inputRow);
    }

    async setValue(value: string)
    {
        if (this.isNative)
        {
            this.input.value = DatePickerHelper.toNative(value);
            return;
        }

        if (value)
        {
            this.fp.setDate(value, false);
        }
        else
        {
            this.fp.clear();
        }
    }

    getValue()
    {
        if (this.isNative)
        {
            return DatePickerHelper.fromNative(this.input.value);
        }

        const value = this.input.value;
        return value === "" ? null : value;
    }

    onChange(action: () => void)
    {
        this.onChangeAction = action;
    }

    focus()
    {
        this.input.focus();
    }

    /**
     * Auto-formats the typed value as D.M.YYYY. Splits the value into day, month
     * and year segments (max 2, 2 and 4 digits) and inserts a dot automatically
     * when a segment fills up, so typing 22062026 yields 22.06.2026. A dot the
     * user types ends the current segment early, so single-digit days and months
     * such as 1.1.2011 are also possible. The caret is repositioned to follow the
     * digit it was on, accounting for the dots inserted before it.
     */
    private maskDateInput()
    {
        const el = this.input;
        const caretEnd = el.selectionEnd ?? el.value.length;
        const digitsBeforeCaret = el.value.slice(0, caretEnd).replace(/\D/g, "").length;

        const max = [2, 2, 4];
        const raw = el.value.replace(/[^\d.]/g, "");
        const segs = [""];
        for (const ch of raw)
        {
            if (ch === ".")
            {
                if (segs.length < max.length && segs[segs.length - 1].length > 0)
                {
                    segs.push("");
                }
                continue;
            }

            let cur = segs.length - 1;
            if (segs[cur].length >= max[cur] && segs.length < max.length)
            {
                segs.push("");
                cur++;
            }
            if (segs[cur].length < max[cur])
            {
                segs[cur] += ch;
            }
        }

        const out = segs.join(".");
        if (out === el.value)
        {
            return;
        }

        el.value = out;

        let caret = 0;
        let seen = 0;
        while (caret < out.length && seen < digitsBeforeCaret)
        {
            if (/\d/.test(out[caret]))
            {
                seen++;
            }
            caret++;
        }
        while (caret < out.length && out[caret] === ".")
        {
            caret++;
        }
        el.setSelectionRange(caret, caret);
    }

    private notifyChange()
    {
        if (this.onChangeAction)
        {
            this.onChangeAction();
        }
    }
}
